import React, { useState, useEffect } from "react";

import { LeaveBalanceType } from "../../services/types/ComponentTypes";
import { apiService } from "../../services/api";
import { useAppSelector } from "../../state/hooks";
import { selectUser } from "../../state/selectors";

type LeaveBalanceProps = {
  title?: string;
  leaveBalances?: LeaveBalanceType[];
  apiname?: string;
  config?: any;
};

const LeaveBalance: React.FC<LeaveBalanceProps> = ({
  title = "Leave Balance",
  leaveBalances: propLeaveBalances = [],
  apiname,
  config
}) => {
  const user = useAppSelector(selectUser);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalanceType[]>(propLeaveBalances);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch data from API if apiname is provided
  useEffect(() => {
    const apiEndpoint = apiname || config?.apiname;
    console.log("📋 LeaveBalance useEffect triggered:", {
      apiname,
      configApiname: config?.apiname,
      apiEndpoint,
      propLeaveBalancesCount: propLeaveBalances?.length || 0
    });

    if (apiEndpoint) {
      console.log("📋 LeaveBalance: Fetching data from API:", apiEndpoint);
      setLoading(true);
      setError(null);

      // Use employee table id for leave balance API (same as attendance); fallback to user id for backward compatibility
      const employeeId = user?.employee_id ?? user?.id;
      const year = new Date().getFullYear();

      console.log("📋 LeaveBalance: User data:", { employeeId, year });

      // Build query parameters
      const queryParams: Record<string, any> = {};
      if (employeeId && employeeId !== "000000") {
        queryParams.employee_id = employeeId;
        console.log("📋 LeaveBalance: Using employee_id from Redux:", employeeId);
      } else {
        console.warn("📋 LeaveBalance: No valid employee_id found in Redux!");
      }
      queryParams.year = year;

      // Make API call only if we have employee_id (to avoid 422)
      if (!queryParams.employee_id) {
        console.warn("📋 LeaveBalance: Skipping API call due to missing employee_id");
        setLoading(false);
        return;
      }

      // Construct endpoint path
      let endpoint = apiEndpoint;
      if (!endpoint.startsWith("/")) {
        endpoint = `/g360/${endpoint}`;
      }

      console.log("📋 LeaveBalance: Making API call:", { endpoint, queryParams });

      apiService
        .get(endpoint, queryParams)
        .then((response) => {
          console.log("📋 LeaveBalance: API response received:", response);

          // Handle different response formats
          if (response && typeof response === "object") {
            if (response.success !== undefined && response.data) {
              // Module-style response with data property
              let balances: any[] = [];

              if (Array.isArray(response.data)) {
                balances = response.data;
              } else if (response.data && typeof response.data === 'object') {
                // Check if data is nested inside data field or other common fields
                if (Array.isArray(response.data.data)) {
                  balances = response.data.data;
                } else if (Array.isArray(response.data.leave_balances)) {
                  balances = response.data.leave_balances;
                } else if (Array.isArray(response.data.results)) {
                  balances = response.data.results;
                }
              }

              console.log("📋 LeaveBalance: Parsed balances from response.data:", balances);
              setLeaveBalances(balances);
            } else if (Array.isArray(response)) {
              // Direct array response
              console.log("📋 LeaveBalance: Direct array response:", response);
              setLeaveBalances(response);
            } else {
              console.warn("📋 LeaveBalance: Unexpected API response format:", response);
              setLeaveBalances([]);
            }
          } else {
            console.warn("📋 LeaveBalance: Invalid response type:", typeof response);
            setLeaveBalances([]);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("📋 LeaveBalance: Error fetching data:", err);
          setError(err.message || "Failed to load leave balance");
          setLoading(false);
          setLeaveBalances([]);
        });
    } else {
      console.log("📋 LeaveBalance: No API endpoint provided, using prop data");
      if (propLeaveBalances && propLeaveBalances.length > 0) {
        // Use prop data if no API
        setLeaveBalances(propLeaveBalances);
      } else {
        console.warn("📋 LeaveBalance: No apiname and no prop data provided!");
        setLeaveBalances([]);
      }
    }
  }, [apiname, config?.apiname, propLeaveBalances]);

  // Log to verify data
  console.log("📋 LeaveBalance component received data:", {
    leaveBalancesCount: leaveBalances?.length || 0,
    leaveBalances: leaveBalances,
    loading,
    error
  });

  return (
    <div style={{
      backgroundColor: "hsl(var(--bg-white))",
      border: "1px solid hsl(var(--border-color))",
      borderRadius: "var(--radius)", // 8px
      padding: "var(--space-5)", // 20px equivalent
      marginTop: "var(--space-8)", // 30px equivalent
    }}>
      <h3 className="card-title" style={{
        fontSize: "var(--text-lg)", // 18px
        fontWeight: "var(--font-bold)", // 700
        color: "#1E40AF", // Blue accent
        margin: "0 0 var(--space-5) 0", // 20px bottom
      }}>
        {title}
      </h3>

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {loading ? (
          <div style={{
            textAlign: "center",
            padding: "var(--space-6) var(--space-4)", // 24px 16px
            color: "hsl(var(--text-gray-500))",
            fontSize: "var(--text-sm)"
          }}>
            <p>Loading leave balance...</p>
          </div>
        ) : error ? (
          <div style={{
            textAlign: "center",
            padding: "var(--space-6) var(--space-4)", // 24px 16px
            color: "#EF4444",
            fontSize: "var(--text-sm)"
          }}>
            <p>Error: {error}</p>
          </div>
        ) : leaveBalances.length === 0 ? (
          <div style={{
            textAlign: "center",
            padding: "var(--space-6) var(--space-4)", // 24px 16px
            color: "hsl(var(--text-gray-500))"
          }}>
            <p style={{
              marginBottom: "var(--space-2)", // 8px
              fontSize: "var(--text-base)" // 16px
            }}>
              No leave balances found
            </p>
            <p className="body-text" style={{
              fontSize: "var(--text-sm)", // 14px
              color: "hsl(var(--text-gray-500))"
            }}>
              Contact HR to set up your leave balance
            </p>
          </div>
        ) : (
          leaveBalances.map((balance) => {
            const percentage = Math.round((balance.remaining_days / balance.total_days) * 100);
            const leaveTypeName = balance.leave_types?.name || "Unknown Leave Type";

            return (
              <div key={balance.id} style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                  <span className="body-text" style={{
                    fontSize: "var(--text-sm)", // 14px
                    fontWeight: "var(--font-medium)", // 500
                    color: "hsl(var(--text-foreground))",
                  }}>
                    {leaveTypeName} ({balance.remaining_days}/{balance.total_days})
                  </span>
                  <span className="body-text" style={{
                    fontSize: "var(--text-sm)", // 14px
                    fontWeight: "var(--font-medium)", // 500
                    color: "hsl(var(--text-foreground))",
                  }}>
                    {balance.remaining_days} ({percentage}%)
                  </span>
                </div>
                <div style={{
                  width: "100%",
                  height: "var(--space-2)", // 8px
                  backgroundColor: "hsl(var(--border-color))",
                  borderRadius: "var(--radius-sm)", // 4px
                  overflow: "hidden",
                }}>
                  <div style={{
                    width: `${percentage}%`,
                    height: "100%",
                    backgroundColor: "hsl(var(--color-primary))",
                    borderRadius: "var(--radius-sm)", // 4px
                  }} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LeaveBalance;
